/* ──────────────────────────────────────────────────────────────
   src/hooks/useReservas.ts
   Lista de reservas con React-Query → refresco instantáneo
   Adaptado a backend paginado { meta, items } con autopaginación.
   ────────────────────────────────────────────────────────────── */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import api from '../api/axios';
import type { ReservaFilters } from '../types/ReservaFilters';
import type { ReservaEvt } from '../api/ReservaEvt';
import { useSocket } from '../context/SocketContext';

/* ═════════════════════════════════════════════════════════════
   Helpers locales
   ═════════════════════════════════════════════════════════════ */

type ReservasPaged =
  | {
      meta?: {
        total: number;
        page: number;
        perPage: number;
        totalPages: number;
        desde?: string;
        hasta?: string;
        order?: 'asc' | 'desc';
      };
      items?: any[];
      filas?: any[];
      rows?: any[];
    }
  | any;

const MAX_PER_PAGE = 200; // debe coincidir con el tope del back

const toIdArray = (val: unknown): number[] => {
  if (Array.isArray(val)) return val.map(Number);
  if (typeof val === 'string' && val.trim()) {
    return val
      .split(',')
      .filter(Boolean)
      .map((x) => Number(x));
  }
  return [];
};

const toNumOrUndef = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/** Autopagina el endpoint /reservas hasta obtener todos los items del rango */
async function fetchAllReservas(params: Record<string, any>) {
  let page = 1;
  const perPage = MAX_PER_PAGE;
  const out: any[] = [];

  while (true) {
    const { data } = await api.get<ReservasPaged>('/reservas', {
      params: { ...params, page, perPage, order: 'asc' },
    });

    const chunk: any[] = Array.isArray((data as any)?.items)
      ? (data as any).items
      : Array.isArray((data as any)?.filas)
        ? (data as any).filas
        : Array.isArray((data as any)?.rows)
          ? (data as any).rows
          : Array.isArray(data)
            ? (data as any)
            : [];

    out.push(...chunk);

    const total = Number((data as any)?.meta?.total ?? out.length);
    if (!Number.isFinite(total) || out.length >= total) break;

    page += 1;
    if (page > Math.ceil(total / perPage) + 2) break; // salvaguarda
  }

  return out;
}

/* ═════════════════════════════════════════════════════════════
   Hook
   ═════════════════════════════════════════════════════════════ */

export function useReservas(
  /** AAAA-MM-DD (incluido) */
  desde: string | null,
  /** AAAA-MM-DD (incluido) */
  hasta: string | null,
  /** filtros activos */
  filters: ReservaFilters,
) {
  const qKey = ['reservas', { desde, hasta, ...filters }];

  const { data = [], isFetching: loading } = useQuery<ReservaEvt[]>({
    queryKey: qKey,
    enabled: !!(desde && hasta), // evita llamada sin rango
    queryFn: async () => {
      try {
        const params = {
          desde,
          hasta,
          profesorId: filters.profesorId ?? undefined,
          clienteIdUsr: filters.clienteIdUsr ?? undefined,
          asignaturaId: filters.asignaturaId ?? undefined,
          centroId: filters.centroId ?? undefined,
          curso: filters.curso ?? undefined,
          estado: filters.estado ?? undefined,
          incluirCanceladas:
            filters.incluirCanceladas === undefined
              ? undefined
              : Boolean(filters.incluirCanceladas),
          idProyecto: filters.idProyecto ?? undefined,
          idFormato: filters.idFormato ?? undefined,
          aulaId: (filters as any).aulaId ?? undefined,
          aulaEstado: (filters as any).aulaEstado ?? undefined,
          // page/perPage/order los maneja fetchAllReservas
        };

        const base: any[] = await fetchAllReservas(params);

        const arr: ReservaEvt[] = base.map((r) => {
          // id del proyecto (aceptar múltiples alias)
          const idProyecto =
            toNumOrUndef((r as any).idProyecto) ?? toNumOrUndef((r as any).proyecto_id);

          // id/nombre de formato
          const idFormato =
            toNumOrUndef((r as any).idFormato) ??
            toNumOrUndef((r as any).formatoId) ??
            toNumOrUndef((r as any).id_formato);
          const nombreFormato =
            (r as any).nombreFormato ??
            (r as any).formatoNombre ??
            (r as any).nombre_formato ??
            (r as any).formato ??
            undefined;

          // id/nombre de asignatura
          const idAsignatura =
            toNumOrUndef((r as any).idAsignatura) ??
            toNumOrUndef((r as any).asignaturaId) ??
            toNumOrUndef((r as any).id_asignatura);
          const nombreAsignatura =
            (r as any).nombreAsignatura ??
            (r as any).asignaturaNombre ??
            (r as any).nombre_asignatura ??
            (r as any).asignatura ??
            undefined;

          // id/nombre de centro
          const idCentro =
            toNumOrUndef((r as any).idCentro) ??
            toNumOrUndef((r as any).centroId) ??
            toNumOrUndef((r as any).id_centro);
          const nombreCentro =
            (r as any).nombreCentro ??
            (r as any).centroNombre ??
            (r as any).nombre_centro ??
            (r as any).centro ??
            undefined;

          // normalizar idCliente desde idClienteUsr si fuera necesario
          const idClienteNorm =
            toNumOrUndef((r as any).idCliente) ??
            toNumOrUndef((r as any).id_cliente) ??
            toNumOrUndef((r as any).idClienteUsr) ??
            toNumOrUndef((r as any).id_cliente_usr) ??
            0;

          // otros campos usados en UI
          const language = (r as any).language ?? (r as any).idioma ?? (r as any).lang ?? undefined;
          const link = (r as any).link ?? (r as any).linkAddress ?? (r as any).enlace ?? undefined;
          const examBoard = (r as any).examBoard ?? (r as any).examinationBoard ?? undefined;
          const studentYear =
            (r as any).studentYear ?? (r as any).curso ?? (r as any).year ?? undefined;

          return {
            ...r,

            /* estado (DB-first) */
            estadoCode: (r as any).estadoCode ?? (r as any).estado_code ?? undefined,
            estadoColor:
              (r as any).estadoColor ?? (r as any).estado_color ?? (r as any).color ?? null,

            /* proyecto relacional */
            idProyecto,
            nombreProyecto:
              (r as any).nombreProyecto ??
              (r as any).proyectoNombre ??
              (r as any).nombre_proyecto ??
              undefined,
            codigoProyecto:
              (r as any).codigoProyecto ??
              (r as any).proyectoCodigo ??
              (r as any).codigo_proyecto ??
              undefined,
            proyecto: (r as any).proyecto ?? undefined, // legacy

            /* formato normalizado */
            idFormato,
            nombreFormato,

            /* asignatura normalizada */
            idAsignatura,
            nombreAsignatura,

            /* centro normalizado */
            idCentro,
            nombreCentro,

            /* entidades normalizadas */
            idCliente: Number(idClienteNorm),

            /* arrays & números coherentes */
            alumnosIds: toIdArray((r as any).alumnosIds ?? (r as any).alumnos_ids),
            numAlumnos: Number((r as any).numAlumnos ?? 1),
            firmasRequeridas:
              (r as any).firmasRequeridas !== null && (r as any).firmasRequeridas !== undefined
                ? Number((r as any).firmasRequeridas)
                : null,

            /* boolean flags */
            incidencia: Boolean((r as any).incidencia),
            firmada: Boolean((r as any).firmada),

            /* internos opcionales (solo staff) */
            comentariosInternos:
              (r as any).comentariosInternos ?? (r as any).comentarios_internos ?? undefined,

            /* agenda cliente (opcional) */
            firmadoCliente: (r as any).firmadoCliente ?? (r as any).firmado_cliente ?? undefined,

            /* campos legibles para UI */
            language,
            link,
            examBoard,
            studentYear,
          } as ReservaEvt;
        });

        return arr;
      } catch (err) {
        const axiosErr = err as AxiosError;
        const status = axiosErr.response?.status;

        // 401/403 ⇒ acceso denegado (agenda cliente sin reservas, etc.)
        if (status === 401 || status === 403) {
          return []; // sin lanzar error
        }

        // otros errores se propagan para que React-Query los maneje
        throw err;
      }
    },
    staleTime: 60 * 1000, // 1 min - el socket invalida cuando hay cambios
    refetchOnWindowFocus: true,
  });

  // Escuchar invalidaciones en tiempo real via WebSocket compartido
  const { socket } = useSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket) return;

    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['reservas'] });
    };

    socket.on('reserva-invalidate', handler);
    return () => {
      socket.off('reserva-invalidate', handler);
    };
  }, [socket, queryClient]);

  return { rows: data, loading };
}
